// CADENCE-1 — **THE NEVER-PAID CLIFFHANGER CADENCE.** Her marketing order, 2026-08-21.
//
// >>> THIS FILE IMPORTS NOTHING, WHICH IS THE ENTRY REQUIREMENT THAT LETS A `.mjs` GATE LOAD AND RUN
// IT. <<< Every decision the machine makes — who is in it, when her next one is due, what her
// subject says, whether the paragraph really cliffhangs, when she leaves — is a pure function here
// rather than a condition inside a store that reaches the pool (R146: reach is transitive).
//
// WHAT WAS ALREADY BUILT AND IS **NOT** REBUILT (R19 — a change with no defect under it is its own
// defect):
//   * the READING is `produceMorningLine`, chart-derived, with MG2's accuracy gate and MG3's
//     placement gate riding — the same engine her members' mornings come from;
//   * the CLIFFHANG is `FUNNEL_TEASER_SYSTEM`, which already says *IT CUTS OFF MID-REVELATION* and
//     *if this paragraph feels FINISHED, it has failed* — her funnel law, written for exactly this;
//   * the LANDING is `morningUrl(guidanceId)` -> `?daily=<id>`, and its security property is already
//     what she asked for: the store reads `WHERE id = $1 AND user_id = $2`, so **the client cannot
//     ask for somebody else's reading** (LR1 ruling 5). Nothing here re-implements it.

// ================================================================================================
// 1. HER AUDIENCE.
// ================================================================================================
/** The reasons a lead is not in the cadence, each its own fact (R18) so the ledger can say WHICH. */
export const CADENCE_EXCLUSIONS = [
  "no-birth-data",   // no chart exists, so there is nothing personal to read
  "paid",            // her clause: a lead leaves THE MOMENT she pays, mid-sequence
  "frozen",          // L15's freeze — she has paid and is between cards, not a stranger
  "unsubscribed",    // BROADCAST's opt-out, honoured instantly and permanently
  "undeliverable",   // the signup-address round's flag: this house cannot reach her at all
  "harness",         // W11-1: a night of walkthroughs may never read as her warm list
  "bounced",         // her self-retire clause
  "converted",       // her self-retire clause
];
export const cadenceExclusionValid = (k) => CADENCE_EXCLUSIONS.includes(String(k || ""));

// ================================================================================================
// 2. HER PACING, AND THE AMENDMENT IS THE WHOLE OF IT.
// ================================================================================================
/**
 * >>> EVERY OTHER DAY FOR THE FIRST 21 DAYS, THEN WEEKLY. <<< Her amendment, and its reason is
 * hers: *"the ease-off protecting sender reputation and the stubborn lead's patience."*
 *
 * **THE DAY COUNTED IS THE LEAD'S OWN, FROM HER FIRST CADENCE SEND** — never the calendar and never
 * the wave's day. A lead who joined the cadence three weeks after another is on her own week one.
 */
export const CADENCE_EVERY_OTHER_DAYS = 2;
export const CADENCE_WEEKLY_DAYS = 7;
export const CADENCE_EASE_OFF_DAY = 21;

/**
 * The gap, in days, before this lead's NEXT send. Asked of how far into HER OWN sequence she is.
 *
 * **A lead who has never been sent to is due IMMEDIATELY** (gap 0) — that is what an opening wave
 * is. The ease-off is a `>=` on the day she is entering, so day 21 itself is already weekly rather
 * than one last two-day hop past the boundary.
 */
export function cadenceGapDays(dayInSequence) {
  const day = Number(dayInSequence);
  if (!Number.isFinite(day) || day < 0) return CADENCE_EVERY_OTHER_DAYS;
  return day >= CADENCE_EASE_OFF_DAY ? CADENCE_WEEKLY_DAYS : CADENCE_EVERY_OTHER_DAYS;
}

/**
 * >>> HER "STAGGERED BY SIGNUP DATE, NOT ONE GIANT BLAST DAY" CLAUSE, AS ARITHMETIC. <<<
 *
 * Two leads who signed up on different days land on different days forever, because the offset is a
 * property of the lead rather than of the run. The stagger is over the WHOLE gap, so an every-other-
 * day cohort splits across two days and a weekly cohort across seven — which is what protects the
 * sending reputation her amendment is about.
 *
 * **IT IS A REAL DATE OR IT IS NOTHING** (R53/R136/R137): a signup stamp that is not a date answers
 * offset 0 rather than `NaN`, because `NaN % 2` is `NaN` and a lead whose offset is `NaN` is never
 * due — she would sit in the sequence forever, silently, receiving nothing.
 */
export function cadenceStagger(signupMs, gapDays = CADENCE_EVERY_OTHER_DAYS) {
  const gap = Number(gapDays);
  if (!Number.isFinite(gap) || gap < 1) return 0;
  const ms = signupMs instanceof Date ? signupMs.getTime() : Number(signupMs);
  if (!Number.isFinite(ms)) return 0;
  const day = Math.floor(ms / 86400000);
  return ((day % gap) + gap) % gap;
}

/**
 * >>> IS THIS LEAD DUE, AND WHY NOT. <<< Four answers rather than two (R18), because *she has left
 * the sequence*, *she is not due yet* and *she is due* send the machine three different ways, and a
 * lead the reader could not place must never be treated as due — a wrongly-due lead is a mail she
 * did not consent to the pace of, which is the one thing a sending reputation cannot take.
 *
 * `todayIndex` and `lastSentIndex` are whole DAYS (a day number, not a timestamp), so the caller
 * decides the timezone once and this law never has an opinion about midnight.
 */
export const CADENCE_DUE = Object.freeze({
  DUE: "due", WAITING: "waiting", RETIRED: "retired", UNKNOWN: "unknown",
});

export function cadenceDueState({
  retiredReason = "", todayIndex = null, firstSentIndex = null, lastSentIndex = null, signupMs = null,
} = {}) {
  if (retiredReason) return CADENCE_DUE.RETIRED;
  if (!Number.isInteger(todayIndex)) return CADENCE_DUE.UNKNOWN;
  // Never sent to: the opening wave. She is due the moment she is eligible.
  if (!Number.isInteger(lastSentIndex)) return CADENCE_DUE.DUE;
  if (!Number.isInteger(firstSentIndex)) return CADENCE_DUE.UNKNOWN;
  const dayInSequence = todayIndex - firstSentIndex;
  if (dayInSequence < 0) return CADENCE_DUE.UNKNOWN;
  const gap = cadenceGapDays(dayInSequence);
  const offset = cadenceStagger(signupMs, gap);
  // Due when enough days have passed AND today is this lead's own slot in the gap.
  if (todayIndex - lastSentIndex < gap) return CADENCE_DUE.WAITING;
  return ((todayIndex % gap) + gap) % gap === offset ? CADENCE_DUE.DUE : CADENCE_DUE.WAITING;
}

// ================================================================================================
// 3. HER SUBJECT, IN HER CLICKBAIT REGISTER, ROTATING.
// ================================================================================================
/**
 * >>> "ROTATING HOOK PATTERNS SO REPEAT EMAILS NEVER LOOK IDENTICAL." <<< Her words.
 *
 * The HOOK is the line the daily rail already produces for the lock screen — her own clickbait
 * register, already gated. What rotates is the FRAME around it, and the frame is chosen by the
 * lead's own send count so two leads on the same evening do not read the same subject either.
 *
 * **A pattern that would render an empty subject is refused rather than sent**: a mail with no
 * subject line is the one shape a spam filter reads as machine-sent, on a rail whose reputation her
 * ease-off amendment exists to protect.
 */
export const CADENCE_SUBJECT_PATTERNS = [
  (hook) => hook,
  (hook) => `${hook} (this is yours)`,
  (hook) => `Something in your chart today: ${lowerFirst(hook)}`,
  (hook) => `I saw this for you: ${lowerFirst(hook)}`,
  (hook) => `${hook} — and it is not over`,
  (hook) => `Read this one, ${lowerFirst(hook)}`,
];

function lowerFirst(s) {
  const t = String(s || "").trim();
  if (!t) return "";
  // Never lower-case a name or an acronym: only a plain capitalised first word folds.
  const first = t.split(/\s+/)[0] || "";
  if (first.length > 1 && first === first.toUpperCase()) return t;
  return t.charAt(0).toLowerCase() + t.slice(1);
}

export const CADENCE_SUBJECT_MAX = 90;

export function cadenceSubject(hook, sendCount = 0) {
  const clean = String(hook || "").trim().replace(/\s+/g, " ");
  if (!clean) return "";
  const n = Number(sendCount);
  const i = Number.isFinite(n) && n >= 0
    ? Math.floor(n) % CADENCE_SUBJECT_PATTERNS.length
    : 0;
  const made = String(CADENCE_SUBJECT_PATTERNS[i](clean) || "").trim();
  if (!made) return "";
  // >>> AND NO EM DASH REACHES A SUBJECT LINE. <<< Her standing ruling, and a subject is the one
  // string the voice gate never sees, because it is composed here rather than generated.
  const flat = made.replace(/\s*[—–]\s*/g, ", ").replace(/,\s*,/g, ",");
  if (flat.length <= CADENCE_SUBJECT_MAX) return flat;
  // A truncated subject reads as a broken send. Fall back to the bare hook, which is already
  // length-disciplined by the daily rail, rather than cutting her words mid-thought.
  return clean.length <= CADENCE_SUBJECT_MAX ? clean : "";
}

// ================================================================================================
// 4. THE CLIFFHANG, VERIFIED DETERMINISTICALLY BEFORE IT LEAVES.
// ================================================================================================
/**
 * >>> HER TEST: *"IT MUST STOP WHERE THEY NEED THE NEXT SENTENCE."* <<<
 *
 * The prompt PREVENTS and this ENFORCES — the house's own split. What a deterministic reader can
 * actually see is the shape of the ending, so that is what is held, and each refusal is its own
 * fact (R18) so a re-render is told what to change:
 *
 *   * it must not end on a SIGN-OFF or a resolution ("in the end", "so trust", "you have got this");
 *   * it must not end on a QUESTION MARK — a question is a complete rhetorical act and reads as an
 *     invitation to reply rather than as an interrupted thought;
 *   * it must not be so short it is a slogan, nor so long it is the reading itself;
 *   * and it must not name the machinery MG3 bans, because this paragraph is composed from a
 *     reading that has already passed that gate and a distiller can put it back.
 *
 * **WHAT IT DELIBERATELY DOES NOT DO IS DEMAND A MISSING FULL STOP.** A cliffhang can end on a
 * complete-looking sentence whose MEANING is unfinished, which is the better cliffhang and the one
 * the prompt asks for; refusing those would refuse her best paragraphs and leave only the ones that
 * end in an ellipsis (R19 — the expensive direction, on the surface the whole cadence turns on).
 */
export const CLIFFHANG_MIN = 200;
export const CLIFFHANG_MAX = 900;

export const CLIFFHANG_CLOSERS = [
  "in the end", "so trust", "you have got this", "you've got this", "remember that",
  "at the end of the day", "and that is the gift", "and that is enough", "good luck",
  "with love", "blessings", "stay open", "trust the process", "everything happens for a reason",
  "you are exactly where you need to be", "and that is the whole of it",
];

export const CLIFFHANG_MACHINERY =
  /\b(ascendant|midheaven|natal|retrograde|conjunct|conjunction|trine|square|sextile|opposition|house cusp|\d{1,2}(st|nd|rd|th) house|degrees? of)\b/i;

export function cliffhangViolations(paragraph) {
  const text = String(paragraph || "").trim();
  const hits = [];
  if (!text) return [{ label: "cliffhang-empty" }];
  if (text.length < CLIFFHANG_MIN) hits.push({ label: "cliffhang-too-short" });
  if (text.length > CLIFFHANG_MAX) hits.push({ label: "cliffhang-too-long" });
  if (/[—–]/.test(text)) hits.push({ label: "cliffhang-em-dash" });
  if (CLIFFHANG_MACHINERY.test(text)) hits.push({ label: "cliffhang-names-machinery" });
  // The ENDING is the whole claim, so it is read off the tail rather than the whole paragraph:
  // "in the end" in the middle of a sentence is ordinary English.
  const tail = text.slice(-140).toLowerCase();
  if (/[?]\s*$/.test(text)) hits.push({ label: "cliffhang-ends-on-a-question" });
  if (CLIFFHANG_CLOSERS.some((c) => tail.includes(c))) hits.push({ label: "cliffhang-signs-off" });
  return hits;
}

export const cliffhangHolds = (p) => cliffhangViolations(p).length === 0;

// ================================================================================================
// 5. THE BUTTON, DECLARED ONCE.
// ================================================================================================
/** Her words, and the one label every cadence email wears. */
export const CADENCE_CTA = "Finish this reading";

// ================================================================================================
// 6. THE SCOREBOARD'S OWN ROSTER (her clause 5).
// ================================================================================================
/**
 * The five figures she watches, in her order. Declared here and read by the store AND by the
 * surface, so the number on her screen and the number the query counts cannot be two answers
 * (R28/R29) — which on a machine she judges spend by is the whole point of it.
 */
export const CADENCE_ROWS = [
  { key: "inSequence", label: "In sequence" },
  { key: "sent", label: "Emails sent" },
  { key: "tapped", label: "Tapped through" },
  { key: "signedIn", label: "Signed back in" },
  { key: "converted", label: "Became members" },
];
export const CADENCE_ROW_KEYS = CADENCE_ROWS.map((r) => r.key);

/**
 * A figure that has not arrived HOLDS STILL rather than rendering a confident zero (R18/R65). On a
 * scoreboard she reads to decide advertising spend, *nobody tapped* and *we could not count* are
 * two different facts and only one of them is about her members.
 */
export function cadenceFigure(value) {
  return typeof value === "number" && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : null;
}
